import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DataTable, DeleteModal } from "CommonComponent";
import { deleteMyPaf, fetchMyPafs } from "actions";
import {
  getEmployeeSignatureStatusBadgeClass,
  getEmployeeSignatureStatusLabel,
  getMyPafStatusLabel,
  getMyPafWorkflowStatusBadgeClass,
  getMyPafWorkflowStatusKey,
} from "pages/AppraisalManagment/utils/myPafHelpers";
import MyPafsColumns from "../component/MyPafsColumns";
import MyPafsRowActions from "../component/MyPafsRowActions";

const MyPafsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appraisalState = useSelector((state) => state.appraisalState);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [processingRowId, setProcessingRowId] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);

  const loadList = useCallback(() => {
    dispatch(fetchMyPafs());
  }, [dispatch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const rows = appraisalState.myPafsList || [];

  const getWorkflowStatusBadge = useCallback((row) => {
    const label = getMyPafStatusLabel(row);
    const className = getMyPafWorkflowStatusBadgeClass(row);
    const statusKey = getMyPafWorkflowStatusKey(row);
    const style =
      statusKey === "auto_approved"
        ? { backgroundColor: "#d6efff", color: "#0b5e8e" }
        : undefined;
    return (
      <span className={className} style={style}>
        {label}
      </span>
    );
  }, []);

  const getSignatureStatusBadge = useCallback((row) => {
    const label = getEmployeeSignatureStatusLabel(row);
    return <span className={getEmployeeSignatureStatusBadgeClass(row)}>{label}</span>;
  }, []);

  const handleView = useCallback(
    (row) => {
      setProcessingRowId(row.id);
      setProcessingAction("view");
      navigate("/my-pafs/view", { state: { recordId: row.id, record: row } });
      setProcessingRowId(null);
      setProcessingAction(null);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (row) => {
      setProcessingRowId(row.id);
      setProcessingAction("edit");
      navigate(`/emp-paf-sign/${row.id}`);
      setProcessingRowId(null);
      setProcessingAction(null);
    },
    [navigate]
  );

  const handleDeleteRequest = useCallback((row) => {
    setRowToDelete(row);
    setOpenDeleteModal(true);
  }, []);

  const hideDeleteModal = useCallback(() => {
    setOpenDeleteModal(false);
    setRowToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!rowToDelete?.id) return;

    setProcessingRowId(rowToDelete.id);
    setProcessingAction("delete");

    try {
      await dispatch(deleteMyPaf(rowToDelete.id));
      hideDeleteModal();
    } catch (error) {
      console.error("Error deleting PAF:", error);
    } finally {
      setProcessingRowId(null);
      setProcessingAction(null);
    }
  }, [dispatch, hideDeleteModal, rowToDelete]);

  const renderRowActions = useCallback(
    (row) => (
      <MyPafsRowActions
        row={row}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
        processingRowId={processingRowId}
        processingAction={processingAction}
      />
    ),
    [
      handleView,
      handleEdit,
      handleDeleteRequest,
      processingRowId,
      processingAction,
    ]
  );

  // Call during render (not inside another useMemo) so Columns' useMemo follows hook rules.
  const columns = MyPafsColumns({
    renderRowActions,
    getWorkflowStatusBadge,
    getSignatureStatusBadge,
  });

  return (
    <div className="main-content-div">
      <DataTable columns={columns} data={rows} serverSide={false} />

      <DeleteModal
        show={openDeleteModal}
        handleModalOpen={setOpenDeleteModal}
        onDelete={confirmDelete}
      />
    </div>
  );
};

export default MyPafsList;
